/**
 * ClientsList — Phase 2A CRM landing page.
 *
 * Sections:
 *   • Top header: title, "New Client" button
 *   • Filters bar: search · tag dropdown · status pill row
 *   • Stats strip: total clients · active · leads · archived
 *   • Table: name, business, email, tags, invoiced/paid/outstanding, status
 *
 * Clicking a row routes to /invoicing/clients/:id (ClientDetail).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Search, Users, Filter, Trash2, ChevronRight,
  Tag as TagIcon, Mail, Phone, Building2,
} from 'lucide-react';
import { GlassCard } from '@/components/dashboard/dashboard-ui';
import NewClientModal from './NewClientModal';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;

const STATUS_PILLS = [
  { id: '',         label: 'All',      color: 'bg-white/10 text-white' },
  { id: 'active',   label: 'Active',   color: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
  { id: 'lead',     label: 'Leads',    color: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/30' },
  { id: 'archived', label: 'Archived', color: 'bg-white/5 text-white/40' },
];

export default function ClientsList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') || '');
  const [status, setStatus] = useState(params.get('status') || '');
  const [tag, setTag]       = useState(params.get('tag') || '');
  const [data, setData]     = useState({ items: [], tags: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/builder/crm/clients`, {
        headers: auth(),
        params: { search: search || undefined, status: status || undefined, tag: tag || undefined },
      });
      setData(data || { items: [], tags: [] });
    } catch (e) {
      toast.error('Could not load clients');
    } finally { setLoading(false); }
  }, [search, status, tag]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set('search', search);
    if (status) next.set('status', status);
    if (tag)    next.set('tag', tag);
    setParams(next, { replace: true });
  }, [search, status, tag, setParams]);

  const stats = useMemo(() => {
    const items = data.items || [];
    return {
      total:    items.length,
      active:   items.filter(c => c.status === 'active').length,
      lead:     items.filter(c => c.status === 'lead').length,
      archived: items.filter(c => c.status === 'archived').length,
      total_invoiced: items.reduce((s, c) => s + (c.metrics?.total_invoiced_cents || 0), 0),
      total_paid:     items.reduce((s, c) => s + (c.metrics?.total_paid_cents || 0), 0),
    };
  }, [data.items]);

  const onCreated = (client) => {
    setData(d => ({ ...d, items: [client, ...d.items.filter(c => c.id !== client.id)] }));
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!(await themedConfirm('Delete this client? Their invoices will remain but become unlinked.'))) return;
    try {
      await axios.delete(`${API}/builder/crm/clients/${id}`, { headers: auth() });
      setData(d => ({ ...d, items: d.items.filter(c => c.id !== id) }));
      toast.success('Client deleted');
    } catch (err) { toast.error('Could not delete'); }
  };

  return (
    <div className="min-h-full px-4 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-7xl mx-auto space-y-6" data-testid="clients-list-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="clients-header">
        <div>
          <Link to="/website-builder/invoicing" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs mb-2" data-testid="back-to-invoicing">
            <ArrowLeft className="w-3.5 h-3.5" /> Invoicing
          </Link>
          <div className="text-xs uppercase tracking-[0.18em] text-violet-300/80 mb-1">Clients</div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Your client roster</h1>
          <p className="text-white/50 mt-2 text-sm">Centralize contacts, invoice history, and notes for every customer you serve.</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-client-btn"
          className="px-4 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.6)]">
          <Plus className="w-4 h-4" /> New Client
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="clients-stats">
        <Stat icon={Users} label="Total" value={stats.total} />
        <Stat label="Active" value={stats.active} accent="emerald" />
        <Stat label="Invoiced" value={fmtMoney(stats.total_invoiced)} accent="violet" />
        <Stat label="Collected" value={fmtMoney(stats.total_paid)} accent="cyan" />
      </div>

      {/* Filters */}
      <GlassCard className="p-4" data-testid="clients-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, business, email" data-testid="search-input"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400" />
          </div>
          <div className="flex gap-1.5">
            {STATUS_PILLS.map(p => (
              <button key={p.id || 'all'} onClick={() => setStatus(p.id)} data-testid={`status-pill-${p.id || 'all'}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${status === p.id ? p.color + ' ring-1 ring-white/20' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>{p.label}</button>
            ))}
          </div>
          {data.tags?.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-white/40" />
              <select value={tag} onChange={e => setTag(e.target.value)} data-testid="tag-filter"
                className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-violet-400">
                <option value="">All tags</option>
                {data.tags.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Table */}
      <GlassCard className="p-0 overflow-hidden" data-testid="clients-table">
        {loading ? (
          <div className="text-center py-16 text-white/40 text-sm">Loading…</div>
        ) : (data.items?.length === 0) ? (
          <EmptyState onNew={() => setShowNew(true)} hasFilters={!!(search || status || tag)} />
        ) : (
          <>
            <div className="grid grid-cols-12 px-5 py-3 bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/40 border-b border-white/5">
              <div className="col-span-4">Client</div>
              <div className="col-span-2">Tags</div>
              <div className="col-span-2 text-right">Invoiced</div>
              <div className="col-span-2 text-right">Outstanding</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1"></div>
            </div>
            {data.items.map(c => (
              <button
                key={c.id}
                onClick={() => navigate(`/website-builder/invoicing/clients/${c.id}`)}
                data-testid={`client-row-${c.id}`}
                className="w-full grid grid-cols-12 px-5 py-3.5 border-b border-white/5 items-center text-left hover:bg-white/[0.03] transition-colors group"
              >
                <div className="col-span-4 min-w-0">
                  <div className="text-white font-medium truncate">{c.business_name || c.name || 'Unnamed client'}</div>
                  <div className="text-xs text-white/40 truncate inline-flex items-center gap-3 mt-0.5">
                    {c.business_name && c.name && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.name}</span>}
                    {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                    {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                  </div>
                </div>
                <div className="col-span-2 flex flex-wrap gap-1">
                  {(c.tags || []).slice(0, 3).map(t => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-violet-500/15 border border-violet-400/25 text-violet-200 text-[10px]">{t}</span>
                  ))}
                  {(c.tags?.length || 0) > 3 && <span className="text-white/30 text-[10px]">+{c.tags.length - 3}</span>}
                </div>
                <div className="col-span-2 text-right text-white/80 text-sm tabular-nums">{fmtMoney(c.metrics?.total_invoiced_cents)}</div>
                <div className="col-span-2 text-right text-amber-200/80 text-sm tabular-nums">{fmtMoney(c.metrics?.outstanding_cents)}</div>
                <div className="col-span-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${
                    c.status === 'active'   ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30' :
                    c.status === 'lead'     ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30' :
                                              'bg-white/5 text-white/40'
                  }`}>{c.status}</span>
                </div>
                <div className="col-span-1 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span onClick={(e) => remove(e, c.id)} data-testid={`client-delete-${c.id}`} role="button" tabIndex={0}
                    className="p-1.5 rounded text-rose-300/70 hover:text-rose-200 hover:bg-rose-500/10 cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></span>
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </div>
              </button>
            ))}
          </>
        )}
      </GlassCard>

      <NewClientModal open={showNew} onClose={() => setShowNew(false)} onCreated={onCreated} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent = 'white' }) => {
  const colorMap = {
    white: 'text-white', emerald: 'text-emerald-300', violet: 'text-violet-300', cyan: 'text-cyan-300',
  };
  return (
    <GlassCard className="p-4" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-white/50">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`text-2xl font-bold mt-1.5 ${colorMap[accent]}`}>{value}</div>
    </GlassCard>
  );
};

const EmptyState = ({ onNew, hasFilters }) => (
  <div className="text-center py-20 px-6" data-testid="clients-empty">
    <div className="w-14 h-14 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-4">
      <Building2 className="w-6 h-6 text-violet-300" />
    </div>
    <h3 className="text-lg font-semibold text-white">{hasFilters ? 'No clients match your filters' : 'Build your client roster'}</h3>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">
      {hasFilters
        ? 'Try clearing the search or filter chips.'
        : 'Add the businesses and people you invoice so every estimate, contract, and reminder lives in one place.'}
    </p>
    {!hasFilters && (
      <button onClick={onNew} data-testid="empty-new-client" className="mt-5 px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center gap-2">
        <Plus className="w-4 h-4" /> Add your first client
      </button>
    )}
  </div>
);
